import type { MailboxInfo } from "owa-client-types";
import { isAccountFeatureEnabled } from "owa-feature-flags";
import { getApp } from "owa-config";
import { getHostHub } from "owa-config/lib/getHostHub";
import { isBootFeatureEnabled } from "owa-metatags/lib/isBootFeatureEnabled";

/**
 * Checks if the Bizchat SSR feature is enabled for the given mailbox. This requires both the
 * 'mon-copilot-bizchat-ocdi' feature and at least one of the SSR features ('mon-copilot-bizchat-ssr' or
 * 'mon-copilot-bizchat-fullscreen-ssr') to be enabled.
 * @param mailboxInfo - The mailbox information to check the feature flags against.
 * @returns True if the Bizchat SSR feature is enabled for the mailbox, false otherwise.
 */
export const isBizchatSSRFeatureEnabledForMailbox = (mailboxInfo: MailboxInfo): boolean => {
    if (!isAccountFeatureEnabled("mon-copilot-bizchat-ocdi", mailboxInfo)) {
        return false;
    }
    
    const hasSSR = isAccountFeatureEnabled("mon-copilot-bizchat-ssr", mailboxInfo);
    const hasFullscreenSSR = isAccountFeatureEnabled("mon-copilot-bizchat-fullscreen-ssr", mailboxInfo);
    const hasTeamsSSR = getHostHub() === "teamshub" &&
        isAccountFeatureEnabled("mon-copilot-bizchat-ssr-teams", mailboxInfo);
    
    if (!hasSSR && !hasFullscreenSSR && !hasTeamsSSR) {
        return false;
    }
    
    // When hosted inside a Teams hub, the Calendar app
    // acquires tokens via MSAL hosted-calendar flow for the SSR path. Gate that variant behind its own boot flag.
    if (hasTeamsSSR) {
        const app = getApp();
        if (app === "Calendar" || app === "CalendarDeepLink") {
            return isBootFeatureEnabled("auth-msaljs-hostedcalendar-i2");
        }
    }
    
    return true;
};
